import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * A report table's per-user layout — which columns show, in what order, at
 * what width — persisted in `localStorage` under a per-page key.
 *
 * Ported from sellokart-cloud. Deliberately NOT server-state: a column
 * layout is a personal display preference, not tenant data, and keeping it
 * local avoids a schema change plus a write on every drag. The trade-off is
 * that it does not follow a user to another browser, which is the accepted
 * cost.
 */

export interface ReportLayoutState {
  /** Column keys the user has hidden. */
  hidden: string[];
  /** Toggleable column keys in display order (pinned columns aren't listed). */
  order: string[];
  /** Column key → pixel width. Unlisted columns use the table default. */
  widths: Record<string, number>;
}

export const REPORT_COLUMN_MIN_WIDTH = 70;
export const REPORT_COLUMN_MAX_WIDTH = 480;

function storageKey(page: string): string {
  return `order-pilot.report-layout.${page}`;
}

function readStored(page: string): Partial<ReportLayoutState> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(page));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as Partial<ReportLayoutState>;
  } catch {
    // A corrupt or unreadable entry (private mode, quota, hand-edited) must
    // fall back to defaults rather than break the page.
    return null;
  }
}

export interface UseReportLayout {
  state: ReportLayoutState;
  isVisible: (key: string) => boolean;
  widthFor: (key: string) => number | undefined;
  setHidden: (key: string, hidden: boolean) => void;
  setAllHidden: (keys: readonly string[], hidden: boolean) => void;
  move: (key: string, toIndex: number) => void;
  resize: (key: string, deltaPx: number) => void;
  reset: () => void;
  /** [toggleableKeys] in the user's saved order, with unknown keys dropped
   *  and newly-added ones appended. */
  orderedKeys: string[];
}

/**
 * @param page          Stable per-page storage key, e.g. `'orders'`.
 * @param toggleableKeys Every column the user may hide/reorder, in default order.
 * @param defaultHidden  Columns hidden until the user says otherwise.
 */
export function useReportLayout(
  page: string,
  toggleableKeys: readonly string[],
  defaultHidden: readonly string[] = [],
): UseReportLayout {
  const [state, setState] = useState<ReportLayoutState>(() => {
    const stored = readStored(page);
    return {
      // A saved-but-empty hidden list is a real choice ("Show all") and is
      // honoured; only the ABSENCE of a saved entry falls back to defaults.
      hidden: stored?.hidden ?? [...defaultHidden],
      order: stored?.order ?? [...toggleableKeys],
      widths: stored?.widths ?? {},
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey(page), JSON.stringify(state));
    } catch {
      // Storage full or blocked — the layout still works for this session.
    }
  }, [page, state]);

  // Keys the user saved, filtered to those that still exist, with any column
  // added since the save slotted in at its DEFAULT relative position rather
  // than dumped at the end — a column shipped between two others (e.g. Time
  // between Customer and Items) must not land after Net Sales just because
  // someone's layout predates it.
  const orderedKeys = useMemo(() => {
    const known = new Set(toggleableKeys);
    const saved = state.order.filter((key) => known.has(key));
    const savedSet = new Set(saved);
    const result = [...saved];
    let lastPlaced: string | null = null;
    for (const key of toggleableKeys) {
      if (savedSet.has(key)) {
        lastPlaced = key;
        continue;
      }
      const insertAt = lastPlaced ? result.indexOf(lastPlaced) + 1 : 0;
      result.splice(insertAt, 0, key);
      lastPlaced = key;
    }
    return result;
  }, [state.order, toggleableKeys]);

  const isVisible = useCallback((key: string) => !state.hidden.includes(key), [state.hidden]);
  const widthFor = useCallback((key: string) => state.widths[key], [state.widths]);

  const setHidden = useCallback((key: string, hidden: boolean) => {
    setState((prev) => ({
      ...prev,
      hidden: hidden ? [...new Set([...prev.hidden, key])] : prev.hidden.filter((k) => k !== key),
    }));
  }, []);

  const setAllHidden = useCallback((keys: readonly string[], hidden: boolean) => {
    setState((prev) => ({
      ...prev,
      hidden: hidden ? [...new Set([...prev.hidden, ...keys])] : prev.hidden.filter((k) => !keys.includes(k)),
    }));
  }, []);

  const move = useCallback(
    (key: string, toIndex: number) => {
      setState((prev) => {
        const known = new Set(toggleableKeys);
        const current = prev.order.filter((k) => known.has(k));
        const withMissing = [...current, ...toggleableKeys.filter((k) => !current.includes(k))];
        const from = withMissing.indexOf(key);
        if (from === -1) return prev;
        const next = [...withMissing];
        next.splice(from, 1);
        next.splice(Math.max(0, Math.min(toIndex, next.length)), 0, key);
        return { ...prev, order: next };
      });
    },
    [toggleableKeys],
  );

  const resize = useCallback((key: string, deltaPx: number) => {
    setState((prev) => {
      const current = prev.widths[key] ?? DEFAULT_COLUMN_WIDTH;
      const next = Math.min(REPORT_COLUMN_MAX_WIDTH, Math.max(REPORT_COLUMN_MIN_WIDTH, current + deltaPx));
      return { ...prev, widths: { ...prev.widths, [key]: next } };
    });
  }, []);

  const reset = useCallback(() => {
    setState({ hidden: [...defaultHidden], order: [...toggleableKeys], widths: {} });
  }, [defaultHidden, toggleableKeys]);

  return { state, isVisible, widthFor, setHidden, setAllHidden, move, resize, reset, orderedKeys };
}

/** Starting width for a column the user has never dragged. */
export const DEFAULT_COLUMN_WIDTH = 160;
